import { useMatomo } from "@jonkoops/matomo-tracker-react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import addHighChartsMore from "highcharts/highcharts-more";
import FileChartOutline from "mdi-material-ui/FileChartOutline";
import { useEffect, useRef, useState } from "react";
import { getChartOptions } from "./ChartOptions";
import { exportChartsToPdf } from "./utils";

addHighChartsMore(Highcharts);

// add sticky annotation when a point is clicked
Highcharts.addEvent(Highcharts.Point, "click", function () {
  if (this.series.options.className.indexOf("popup-on-click") !== -1) {
    const chart = this.series.chart;
    const series = this.series.chart.options.series;
    const values = series
      .map((ser) => {
        if (ser.data[this.index]) {
          return {
            seriesName: ser.name,
            yValue: ser.data[this.index][1].toFixed(2),
          };
        }
        return null;
      })
      .filter((serObg) => serObg);

    const yText = values
      .map((serObj) => serObj.seriesName + ": " + serObj.yValue + "<br />")
      .toString()
      .replaceAll(",", "");

    const text = `<b>${this.x.toFixed(2)}h</b><br/>${yText}`;

    const id = "sticky_" + this.plotX + "_" + this.plotY;

    chart.addAnnotation({
      id: id,
      plotX: this.plotX,
      plotY: this.plotY,
      labels: [
        {
          point: {
            x: this.plotX,
            y: this.plotY,
          },
          text: text,
        },
      ],
      events: {
        click: () => {
          //memoize id to identify the clicked annotation
          chart.removeAnnotation(id);
        },
      },
    });
  }
});

require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/offline-exporting")(Highcharts);
require("highcharts/modules/annotations")(Highcharts);

const Chart = ({
  patientData,
  creatData,
  popHi,
  popMean,
  popRange,
  patientConcData,
  simConcData,
  criticalWindowData,
}) => {
  const chart = useRef();
  const { trackEvent } = useMatomo();

  const getCreatinineUnit = () => {
    return patientData.creatunitmml ? " μmol/L" : "mg/dL";
  };

  const ChartOptions = getChartOptions(
    patientData,
    patientConcData,
    creatData,
    simConcData,
    popHi,
    popMean,
    popRange,
    getCreatinineUnit,
    trackEvent,
  );

  const downloadChart = () => {
    exportChartsToPdf(criticalWindowData, patientData);
  };

  const [highlightTime, setHighlightTime] = useState();

  const handleHighlight =
    (name) =>
    ({ target: { value } }) => {
      if (name === "time") {
        setHighlightTime(value);
      }
    };

  const highlightInChart = () => {
    const chartRef = chart.current.chart;
    // series with points defined
    const series = chartRef.series;
    let indexAtTime = 0;
    let pointAtIndex = {};
    // look for data in population mean
    series.forEach((ser) => {
      if (ser.name === "Population mean") {
        indexAtTime = ser.data.findIndex((val) => {
          if (parseFloat(val.x.toFixed(2)) >= highlightTime) {
            return val.x;
          }
          return null;
        });
        pointAtIndex = ser.data[indexAtTime];
      }
    });

    // fire the click event. We have an click event listener on the point already
    pointAtIndex.firePointEvent("click");
  };

  // a placeholder attached to the chart instance for the sticky tooltips
  useEffect(() => {
    chart.current.chart.stickyTooltips = [];
  }, []);

  useEffect(() => {
    trackEvent({
      category: "Chart Load",
      action: "Chart Component Loaded",
      name: "Chart Component Loaded",
    });
  }, [trackEvent]);

  return (
    <>
      <Grid item xs={12} lg={9}>
        <Box className="mtx-report-chart">
          <HighchartsReact
            highcharts={Highcharts}
            options={ChartOptions}
            ref={chart}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            mt: 2,
          }}
        >
          <Tooltip title="Highlight time in chart.">
            <TextField
              label="Highlight Time"
              name="time"
              value={highlightTime}
              onChange={handleHighlight("time")}
              sx={{
                width: { xs: "100%", sm: 200 },
              }}
              margin="dense"
            />
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              trackEvent({
                category: "User Interaction",
                action: "Highlight Time In Chart",
                name: "Highlight Time",
              });
              highlightInChart();
            }}
            sx={{
              width: { xs: "100%", sm: "auto" },
              mt: { xs: 1, sm: 0 },
              ml: { xs: 0, sm: 2 },
            }}
          >
            <Typography>Highlight</Typography>
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12} lg={3}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Elimination Threshold</Typography>
          <Typography variant="body1">
            <strong>{criticalWindowData.upper.conc}</strong> μM At{" "}
            <strong>{criticalWindowData.upper.time.toFixed(1)}</strong> h
          </Typography>
          <Typography variant="body1">
            <strong>{criticalWindowData.lower.conc}</strong> μM At{" "}
            <strong>{criticalWindowData.lower.time.toFixed(1)}</strong> h
          </Typography>
          <Typography variant="body1">
            AUC: <strong>{criticalWindowData.auc.toFixed(1)}</strong> μM*h
          </Typography>
          <Divider variant="middle" sx={{ my: 2 }} />
          <Tooltip title="Download Report">
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                trackEvent({
                  category: "User Interaction",
                  action: "Download Report",
                  name: "Download Report",
                });
                downloadChart();
              }}
              sx={{
                mt: { xs: 2, lg: 4 },
                display: "inline-flex",
                alignItems: "center",
              }}
              startIcon={<FileChartOutline />}
            >
              Download Report
            </Button>
          </Tooltip>
        </Paper>
      </Grid>
    </>
  );
};

export default Chart;
